/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import LuiElement from './lui-element'
import cssResetLocal from 'reset.local.scss'
import { generateId } from './lui-id'

export default class LuiBase extends LuiElement {
  static get cssClassToggles() {
    return []
  }

  static get attributeToggles() {
    return []
  }

  static get css() {
    return ''
  }

  static get template() {
    return ''
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  static _addCssClassToggles(list) {
    this._cssClassToggles = {}
    if (!list) {
      return []
    }
    if (Array.isArray(list)) {
      list.forEach((a) => this._addCssClassToggle(a))
      return list
    }
    Object.keys(list).forEach((a) => this._addCssClassToggle(a, list[a]))
    return Object.keys(list)
  }

  static _addCssClassToggle(attribute, cssClass) {
    cssClass = cssClass || this.modifierCssClass(attribute)
    this._cssClassToggles[attribute] = cssClass
  }

  static get observedAttributes() {
    const attributes = Object.getOwnPropertyNames(this.prototype).map((attribute) => {
      if (attribute.match(/Changed$/)) {
        return attribute.replace(/Changed$/, '')
      }
    }).filter((i) => i !== undefined)
    return [
      ...attributes,
      ...this._addCssClassToggles(this.cssClassToggles),
      ...this.attributeToggles,
    ]
  }

  constructor(props = {}) {
    super()
    this._uid = generateId(this.constructor.name)
    this._isFirstTimeConnected = true
    Object.assign({mode: 'open'}, props)
    this.shadowRoot = this.attachShadow(Object.assign({mode: 'open'}, props))
    this.render()
    this._addBooleanSupport()
  }

  render() {
    let css = cssResetLocal + this.constructor.css
    if (window.lui.applyCssPolyfill) {
      css = window.lui.applyCssPolyfill(css, this)
    }
    this.shadowRoot.innerHTML += `<style>${css}</style>${this.constructor.template}`
  }

  connectedCallback() {
    if (this._isFirstTimeConnected) {
      this.seedElement = this._getSeed()
      this.withoutTransition(() => {
        this.init()
        this._initialized = true
      })
    }
    this._isFirstTimeConnected = false

    if (this.constructor.hasLuiElementsInShadowDom) {
      const styles = window.getComputedStyle(this)
      const themeCascade = styles.getPropertyValue('--lui-theme-cascade')
      if (themeCascade) {
        const themeCss = window.lui.themeCss[themeCascade]
        const style = document.createElement('style')
        style.textContent = themeCss
        this.shadowRoot.insertBefore(style, this.shadowRoot.firstChild)
      }
    }

    this.onConnected()

    // #ifndef DEBUG
    if (!this.seedElement) {
      throw new Error('The attribute seedelement was not set! Please set a seedelement for your class ' + this.constructor.name)
    }
    // #endif
  }

  onConnected() {
  }

  disconnectedCallback() {
    this.onDisconnected()
  }

  onDisconnected() {
  }

  init() {
  }

  attributeChangedCallback(attrName, oldVal, newVal) {
    this._applyCssClassToggle(attrName, oldVal)
    if (this.constructor.attributeToggles.includes(attrName)) {
      this[newVal === null ? '_removeSeedAttribute' : '_addSeedAttribute'](attrName, newVal)
    }
    this._callAttributeChangedCallback(attrName, oldVal, newVal)
  }

  _applyCssClassToggle(attrName, oldVal) {
    const cssClass = this.constructor._cssClassToggles[attrName]
    if (cssClass) {
      this.withoutTransition(() => {
        this.toggleCssClass(cssClass, oldVal === null)
      })
    }
  }

  withoutTransition(withoutTransitionCallback) {
    return new Promise((resolve, reject) => {
      this.delay(() => {
        this.classList.add('lui-without-transition')
        withoutTransitionCallback()
        this.delay(() => {
          if (this._initialized) {
            this.classList.remove('lui-without-transition')
          }
          resolve()
        })
      })
    })
  }

  _callAttributeChangedCallback(attrName, oldVal, newVal) {
    const func = this[attrName + 'Changed']
    if (func) {
      func.call(this, oldVal, newVal)
    }
  }

  toggleCssClass(cssClass, state) {
    this.classList[state ? 'add' : 'remove'](cssClass)
  }

  _getSeed() {
    const seed = this.shadowRoot.querySelector('[seedelement]')
    return seed || this
  }

  toggleModifier(name, state) {
    this.toggleCssClass(this.constructor.modifierCssClass(name), state)
  }

  // delayed call to prevent early execution
  delay(callback, timeout = 1) {
    setTimeout(() => callback.call(this), timeout)
  }

  poll(callback, conditionFn, timeout = 101, interval = 10) {
    const endTime = new Date().getTime() + timeout

    const _poll = () => {
      if (conditionFn.call(this)) {
        callback.call(this)
        return
      }
      if (new Date().getTime() < endTime) {
        setTimeout(_poll, interval)
      }
    }

    _poll.call(this)
  }

  debounce(callback, interval = 100, immediate = false) {
    let timeout
    return () => {
      const context = this
      const args = arguments
      const later = function() {
        timeout = null
        if (!immediate) callback.apply(context, args)
      }
      const callNow = immediate && !timeout
      clearTimeout(timeout)
      timeout = setTimeout(later, interval)
      if (callNow) callback.apply(context, args)
    }
  };

  _addSeedAttribute(name, value) {
    if (this.seedElement === this) return
    if (this.seedElement) {
      this.seedElement.setAttribute(name, value || '')
    } else {
      this._seedAttributes = this._seedAttributes || []
      this._seedAttributes[name] = value || ''
    }
  }

  _removeSeedAttribute(name) {
    if (this.seedElement) {
      this.seedElement.removeAttribute(name)
    } else {
      this._seedAttributes = this._seedAttributes || []
      const index = this._seedAttributes.indexOf(name)
      if (index > -1) this._seedAttributes.splice(index, 1)
    }
  }

  // TODO LUI-1045: Extract React specific behavior (unnecessary wrapper checking for 'false' value)
  _addBooleanSupport() {
    this.nativeHasAttribute = this.hasAttribute

    this.hasAttribute = (name) => {
      return this.nativeHasAttribute(name) && !(LuiBase.isBooleanAttribute(name) && this.getAttribute(name) === 'false')
    }
  }

  get shadowRoot() {
    return this._shadowRoot
  }

  set shadowRoot(value) {
    this._shadowRoot = value
  }

  get seedElement() {
    return this._seedElement
  }

  set seedElement(value) {
    const className = LuiBase.dasherize(this.constructor.is) + '__seed'
    if (this._seedElement) this._seedElement.classList.remove(className)
    this._seedElement = value
    this.seedElement.classList.add(className)
    if (this._seedAttributes) {
      Object.keys(this._seedAttributes).forEach((key) => {
        this.seedElement.setAttribute(key, this._seedAttributes[key])
      })
    }
  }

  get initialized() {
    return this._initialized
  }

  get uid() {
    return this._uid
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-base/lui-base.js
// module id = 3
// module chunks = 0 1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38